import * as React from 'react';
import cn from 'classnames';
import styles from './CollectionCard.module.scss';
import messages from './messages';
import { Copy, DotsThreeVertical, Eye, FileArrowDown, ShareNetwork, Stack, TrashSimple, User, UserCircle, Users } from 'src/common/icons';
import { useHistory, useLocation } from 'react-router-dom';
import { useDeleteCollectionMutation } from 'src/api/mutations/collectionMutations';
import { useAlerts } from 'src/common/AlertManager';
import useErrorMessages from 'src/hooks/useErrorsMessage';
import { useIntl } from 'react-intl';
import { getCollectionDownloadUrl } from 'src/api/functions/collectionsApi';
import { useAccessToken } from 'src/hooks/useAccessToken';
import Dropdown from '../Dropdown';
import Loader from '../Loader';
import { Collection } from 'src/types/TCollection';
import moment from 'moment';
import { getThumbnailUrl } from 'src/utils/previews';
import useModal from 'src/hooks/useModal';
import ConfirmDuplicateCollectionModal from '../ConfirmDuplicateCollectionModal';
import AddAssetsToShare from '../AddAssetsToShare';
import { useAuthUser } from 'react-auth-kit';
import useRoleManager from 'src/hooks/useRoleManager';

type CollectionCardProps = {
  collection: Collection;
  className?: string;
};

export const CollectionCard: React.FC<CollectionCardProps> = ({ className, collection, ...rest }) => {
  const { id, name, assets, last_update, group_access, description } = collection;

  const auth = useAuthUser();
  const history = useHistory();
  const userEmail = auth() ? auth()!.userEmail : '';

  const { showAlert, showSuccess } = useAlerts();
  const { getErrorMessage } = useErrorMessages();
  const { formatMessage } = useIntl();
  const [isPreparingDownload, setIsPreparingDownload] = React.useState(false);
  const accessToken = useAccessToken();
  const { showModal } = useModal();
  const { isUser } = useRoleManager();

  const deleteCollectionMutation = useDeleteCollectionMutation(id, {
    onSuccess: () => {
      showSuccess(formatMessage(messages.collectionDeleted));
    },
    onError: (error) => {
      showAlert(getErrorMessage(error.message));
    },
  });

  const goToCollection = () => {
    history.push(`/collection/${id}`);
  };

  const handleDownloadCollection = async () => {
    if (isPreparingDownload) return;

    try {
      setIsPreparingDownload(true);
      const url: any = await getCollectionDownloadUrl(id, accessToken);

      if (url.status >= 400) {
        showAlert(`Download not available at the moment. Please try again later.`);
        setIsPreparingDownload(false);
        return;
      }

      window.open(url);
      setIsPreparingDownload(false);
      
    } catch (error) {
      showAlert(`Download not available at the moment. Please try again later.`);
      setIsPreparingDownload(false);
    }finally{
      setIsPreparingDownload(false);
    }
  };

  const handleDeleteCollection = () => deleteCollectionMutation.mutateAsync(undefined);
  const handleDuplicateCollection = () =>
    showModal(
      <ConfirmDuplicateCollectionModal
        title={`Duplicate collection ${name}`}
        sourceId={id}
        name={`${name} (copy)`}
        description={description}
        groupAccess={group_access}
      />,
    );

  const handleShareCollection = () => {
    if (assets.length === 0) return;
    showModal(<AddAssetsToShare assetIds={assets} />);
  };

  // Actions menu (`...`). Replaces the legacy hover-only overlay on
  // both desktop and mobile so the same affordance works for touch and
  // pointer devices.
  const menuOptions = React.useMemo(() => {
    const opts: Array<{ key: string; value: React.ReactNode; icon?: React.ReactNode; optionDisabled?: boolean }> = [];
    opts.push({ key: 'view', value: 'View collection', icon: <Eye /> });
    opts.push({
      key: 'download',
      value: 'Download all',
      icon: <FileArrowDown />,
      optionDisabled: assets.length === 0,
    });
    if (!isUser) {
      opts.push({
        key: 'share',
        value: 'Share collection',
        icon: <ShareNetwork />,
        optionDisabled: assets.length === 0,
      });
    }
    opts.push({ key: 'duplicate', value: 'Duplicate', icon: <Copy /> });
    if (userEmail === collection.user_email) {
      opts.push({ key: 'delete', value: 'Delete', icon: <TrashSimple /> });
    }
    return opts;
  }, [assets.length, isUser, userEmail, collection.user_email]);

  const handleMenuClick = (key: string) => {
    switch (key) {
      case 'view':
        goToCollection();
        break;
      case 'download':
        handleDownloadCollection();
        break;
      case 'share':
        handleShareCollection();
        break;
      case 'duplicate':
        handleDuplicateCollection();
        break;
      case 'delete':
        handleDeleteCollection();
        break;
    }
  };

  return (
    <div className={cn([styles.CollectionCard, className])}>
      <div className={styles.collectionImage}>
        <span className={styles.groupCollection}>
          {group_access ? (
            <>
              <Users />
              <span>Group Collection</span>
            </>
          ) : (
            <>
              <User />
              <span>Private Collection</span>
            </>
          )}
        </span>

        {assets.length === 0 && <Stack className={styles.assetPreview} />}
        {assets.length > 0 && (
          <img
            src={getThumbnailUrl({ width: 300, height: 300, id: assets[0], jwt: accessToken })}
            alt="Asset In Collection"
          />
        )}

        <div className={styles.menu} onClick={(e) => e.stopPropagation()}>
          <Dropdown
            variant="menu"
            // Anchor on the left so the menu expands rightward from the
            // top-left trigger; otherwise the popup gets pushed off the
            // viewport for cards in the left column.
            dropdownAnchor="left"
            icon={<DotsThreeVertical />}
            options={menuOptions}
            onClick={handleMenuClick}
          />
        </div>

        <button
          type="button"
          className={styles.tapTarget}
          onClick={goToCollection}
          aria-label={`Open ${name}`}
        />

        {/* A small loader shown when a download is in flight; the
            primary spinner used to live in the overlay download button
            which has been replaced by the actions menu. */}
        {isPreparingDownload && (
          <div className={styles.downloadingIndicator} aria-live="polite">
            <Loader />
          </div>
        )}
      </div>
      <div className={styles.collectionContent}>
        <div className={styles.collectionTitleSection}>
          <h5>{name} </h5>
          <span>{assets.length}</span>
        </div>
        {description && <p className={styles.description}>{description}</p>}
        <div className={styles.collectionInfoSection}>
          <span>Updated {moment(last_update).fromNow()}</span>
        </div>
        {/** TODO: Implement the user avatars */}
        {false && (
          <div className={styles.collectionUsers}>
            {new Array(5).fill(0).map((_, index) => (
              <UserCircle key={index} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CollectionCard;
